import {
  BALANCE_KEY,
  STATEMENT_KEY,
  DASHBOARD_KEY,
  PATIENT_KEY,
  PATIENTS_KEY,
  PAYMENTS_KEY,
} from "@web/shared/constants/query-keys";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type {
  CreatePaymentInput,
  PatientBalance,
  PaymentHolder,
  ReversePaymentInput,
  Statement,
  StatementQuery,
} from "@clinic/shared";
import { billingApi } from "@web/modules/billing/api";


export function usePatientBalance(
  patientId: string,
  enabled = true,
): UseQueryResult<PatientBalance> {
  return useQuery({
    queryKey: [BALANCE_KEY, patientId],
    queryFn: () => billingApi.balance(patientId),
    enabled: enabled && patientId !== "",
  });
}

export function useStatement(
  patientId: string,
  query: StatementQuery,
  enabled = true,
): UseQueryResult<Statement> {
  return useQuery({
    queryKey: [STATEMENT_KEY, patientId, query],
    queryFn: () => billingApi.statement(patientId, query),
    enabled: enabled && patientId !== "",
    placeholderData: (previous) => previous,
  });
}

export function usePaymentHolders(enabled: boolean): UseQueryResult<PaymentHolder[]> {
  return useQuery({
    queryKey: [PAYMENTS_KEY, "holders"],
    queryFn: () => billingApi.paymentHolders(),
    enabled,
  });
}

function useLedgerInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all(
      [BALANCE_KEY, STATEMENT_KEY, PAYMENTS_KEY, PATIENT_KEY, PATIENTS_KEY, DASHBOARD_KEY].map(
        (key) => queryClient.invalidateQueries({ queryKey: [key] }),
      ),
    );
  };
}

export function useCreatePayment() {
  const invalidate = useLedgerInvalidation();

  return useMutation({
    mutationFn: (body: CreatePaymentInput) => billingApi.createPayment(body),
    onSuccess: invalidate,
  });
}

export function useReversePayment() {
  const invalidate = useLedgerInvalidation();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReversePaymentInput }) =>
      billingApi.reversePayment(id, body),
    onSuccess: invalidate,
  });
}

export function useDeletePayment() {
  const invalidate = useLedgerInvalidation();

  return useMutation({
    mutationFn: (id: string) => billingApi.deletePayment(id),
    onSuccess: invalidate,
  });
}
